import { NotesOutlined } from "@mui/icons-material";
import { logger } from "@platform/browser-logger";
import { focusRatesGridLastInput } from "@platform/btx-grid-components";
import {
  ColDef,
  GetMainMenuItems,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  ColumnMovedEvent,
  NavigateToNextCellParams,
  SideBarDef,
  SuppressKeyboardEventParams,
  TabToNextCellParams,
} from "ag-grid-community";
import { RefObject } from "react";

import { getPlanEditorTheme } from "../../../utilities/planEditorTheme";
import { muiIconToElement } from "../../../utilities/muiIconToElement";

import { enforceBenefitsColumnFirst } from "./benefitsColumnInvariant";
import {
  extractCarrierOrderFromEvent,
  extractMovedCarrierIdFromEvent,
  extractPlanOrderFromEvent,
} from "./columnOrderUtils";
import { copyCollapsedSiblingsOnFill } from "./fillCollapsedSiblings";
import { MIN_COLUMN_WIDTH, processCellFromClipboard } from "./gridDefaults";
import { registerOnCellValueChanged } from "./onCellValueChanged";
import { hasManualWidth } from "./useResizedColumnWidths";

import type { RfpCoverage } from "../../../api/types";
import type { CrossCellContext } from "../validation/validators";
import type { CommissionUpdateRequest } from "../../../api/commissionMutation";

interface CycleFillValueParams {
  initialValues: unknown[];
  currentIndex: number;
  currentCellValue: unknown;
}

export const cycleFillValue = (params: CycleFillValueParams): unknown => {
  const { initialValues, currentIndex, currentCellValue } = params;
  const { length } = initialValues;
  if (length === 0) {
    return currentCellValue;
  }
  const normalizedIndex = ((currentIndex % length) + length) % length;
  return initialValues[normalizedIndex];
};

/**
 * Flex is what fills the grid width, and stale state can leave a column pinned
 * without it. Columns the user dragged are pinned deliberately, so skip those.
 */
const restoreColumnFlex = (api: GridApi): void => {
  const state = api
    .getAllGridColumns()
    .filter((column) => !hasManualWidth(api, column.getColId()))
    .map((column) => {
      const { flex } = column.getColDef();
      return {
        colId: column.getColId(),
        flex: typeof flex === "number" && flex > 0 ? flex : null,
      };
    });
  if (state.length > 0) api.applyColumnState({ state });
};

/** Rows whose cells span a plan's network sub-columns; the rest span one. */
const getSpanningRowNodes = (api: GridApi): IRowNode[] => {
  const spanning: IRowNode[] = [];
  api.forEachNode((node) => {
    if (!node.data?.benefit?.networkDependent) spanning.push(node);
  });
  return spanning;
};

const pendingVisibilityReflows = new WeakSet<GridApi>();

/**
 * Refills the width and re-evaluates colSpan after a column is hidden or
 * shown. Only redrawRows rebuilds the per-row column list colSpan decides,
 * and autoHeight makes it slow enough to show the grid's loading state for.
 *
 * It deliberately runs *after* a paint, not before: the rebuild blocks the
 * thread, so the loader has to be drawn first or it never appears. rAF fires
 * ahead of that paint, the timeout after it. Coalesced because hiding a plan
 * turns off each of its sub-columns separately.
 */
const scheduleVisibilityReflow = (api: GridApi): void => {
  if (pendingVisibilityReflows.has(api)) {
    return;
  }
  pendingVisibilityReflows.add(api);
  // PlanEditor drives this same option from its own data-loading state, so
  // hand back what was there rather than clearing it, or a reflow landing
  // mid-load would dismiss that overlay for good.
  const loadingBeforeReflow = api.getGridOption("loading") ?? false;
  api.setGridOption("loading", true);
  requestAnimationFrame(() =>
    setTimeout(() => {
      pendingVisibilityReflows.delete(api);
      if (api.isDestroyed()) {
        return;
      }
      restoreColumnFlex(api);
      api.redrawRows({ rowNodes: getSpanningRowNodes(api) });
      api.setGridOption("loading", loadingBeforeReflow);
    }, 0),
  );
};

export const createGridOptionsWithHandlers = (
  onGridReady: (params: GridReadyEvent) => void,
  getMainMenuItems: GetMainMenuItems,
  onCoverageChange?: (updatedCoverage: RfpCoverage) => void,
  onPlanReorder?: (planOrder: string[]) => void,
  coverage?: RfpCoverage,
  getCurrentCoverage?: () => RfpCoverage | undefined,
  saveGridState?: () => void,
  isInternalUpdateRef?: RefObject<boolean>,
  validateFieldFn?: (
    value: unknown,
    fieldOrCategory: string,
    field?: string,
    context?: CrossCellContext,
  ) => { isValid: boolean; failedValidators: string[] },
  onCommissionChange?: (request: CommissionUpdateRequest) => void,
  sideBar?: SideBarDef | false,
  onCarrierReorder?: (
    carrierIds: string[],
    movedCarrierId: string | null,
  ) => void,
  defaultColDefOverrides?: Partial<ColDef>,
  onManuallyEdited?: (
    planId: string,
    fieldPath: string,
    options?: { isBenchmarkField?: boolean },
  ) => void,
  onAiFieldEdited?: (
    planId: string,
    fieldPath: string,
    newValue: unknown,
    oldValue: unknown,
  ) => void,
  onUserColumnMoved?: (event: ColumnMovedEvent) => void,
): GridOptions => ({
  // Render popup editors relative to document.body so they are never
  // clipped by the grid container's overflow or scroll bounds.
  popupParent: typeof document !== "undefined" ? document.body : undefined,
  cellSelection: {
    handle: {
      mode: "fill",
      setFillValue: (params) => {
        const value = cycleFillValue({
          initialValues: params.initialValues as unknown[],
          currentIndex: params.currentIndex,
          currentCellValue: params.currentCellValue as unknown,
        });
        copyCollapsedSiblingsOnFill(params, value);
        return value;
      },
    },
  },
  theme: getPlanEditorTheme(),
  icons: {
    notesOutlined: () => muiIconToElement(NotesOutlined),
  },
  animateRows: true,
  // stopEditingWhenCellsLoseFocus: true,
  getRowId: (params) => (params.data?.benefitKey as string) || "",
  tooltipShowDelay: 500,
  pagination: false,
  undoRedoCellEditing: true,
  enterNavigatesVerticallyAfterEdit: true,
  tabIndex: 3,
  suppressHeaderFocus: true,
  defaultColDef: {
    resizable: true,
    sortable: false,
    filter: false,
    minWidth: MIN_COLUMN_WIDTH, // Consistent width for aligned grid sync with RatesGrid/SummaryGrid
    suppressHeaderMenuButton: true,
    autoHeight: true,
    flex: 1,
    ...defaultColDefOverrides,
    // Allow Tab key to escape the grid when at boundary cells
    // This prevents focus trapping caused by pinned columns
    suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
      const { event, editing } = params;

      // A rich-text popup editor's contentEditable field owns Enter itself
      // (new paragraph / list item). AG Grid's own popup-editor wrapper also
      // listens for Enter and calls stopEditing regardless of what the
      // editor's own keyboard handling already did with it — suppress that
      // redundant handling here so Enter doesn't close the popup mid-edit.
      if (
        editing &&
        event.key === "Enter" &&
        event.target instanceof HTMLElement &&
        event.target.isContentEditable
      ) {
        return true;
      }

      if (
        editing &&
        (event.key === "ArrowLeft" ||
          event.key === "ArrowRight" ||
          event.key === "ArrowUp" ||
          event.key === "ArrowDown")
      ) {
        return true;
      }

      // Only handle Tab key
      if (event.key !== "Tab") {
        return false;
      }

      const { api } = params;
      const isShiftTab = event.shiftKey;

      // Get all displayed columns
      const allColumns = api.getAllDisplayedColumns();
      const lastColumn = allColumns[allColumns.length - 1];
      const firstUnpinnedColumn = allColumns.find((col) => !col.isPinned());

      // Get the current focused cell
      const focusedCell = api.getFocusedCell();
      if (!focusedCell) {
        return false;
      }

      // Get row count
      const rowCount = api.getDisplayedRowCount();
      const lastRowIndex = rowCount - 1;

      // Forward Tab at last cell of last row: suppress AG Grid handling, let browser take over
      if (
        !isShiftTab &&
        focusedCell.column === lastColumn &&
        focusedCell.rowIndex === lastRowIndex
      ) {
        // Return true to SUPPRESS AG Grid's handling, allowing browser to handle Tab
        return true;
      }

      // Shift+Tab at first unpinned cell of first row: focus RatesGrid's last input
      if (
        isShiftTab &&
        focusedCell.column === firstUnpinnedColumn &&
        focusedCell.rowIndex === 0
      ) {
        // Use explicit data-grid-id identification to find and focus RatesGrid
        event.preventDefault();
        focusRatesGridLastInput();
        return true; // Suppress AG Grid handling
      }

      // For all other cases, don't suppress (let AG Grid handle navigation within the grid)
      return false;
    },
  },
  navigateToNextCell: (params: NavigateToNextCellParams) => {
    const { nextCellPosition } = params;
    return nextCellPosition;
  },
  tabToNextCell: (params: TabToNextCellParams) =>
    params.nextCellPosition ?? false,
  processCellFromClipboard: (params) =>
    processCellFromClipboard(params.node, params.value),
  // State persistence event handlers
  onColumnMoved: (event) => {
    if (event.finished) {
      enforceBenefitsColumnFirst(event.api);
    }

    // Save grid state for persistence
    saveGridState?.();

    // Only persist the order for a genuine user drag. Re-applying columnDefs
    // (e.g. via updateGridOptions) also fires a finished columnMoved, but with
    // source "gridOptionsChanged" and the columns snapped back to their original
    // order — reacting to it would overwrite the real reorder with the old order.
    const isUserColumnMove = event.source === "uiColumnMoved";

    // Lets the surface adjust the dropped columns (e.g. regroup a carrier's
    // versions) before the plan and carrier order are read back below.
    if (event.finished && isUserColumnMove) {
      onUserColumnMoved?.(event);
    }

    // Extract plan order for API callback
    if (event.finished && isUserColumnMove && onPlanReorder) {
      try {
        const planOrder = extractPlanOrderFromEvent(event);

        if (planOrder && planOrder.length > 0) {
          logger.info("[PlanEditor] Column moved - new plan order:", {
            planOrder,
          });
          onPlanReorder(planOrder);
        }
      } catch (error) {
        logger.error(
          "[PlanEditor] Error extracting plan order from column move:",
          error as Error,
        );
      }
    }

    // Extract carrier-level order for carrier sequence reordering (AC1)
    if (event.finished && isUserColumnMove && onCarrierReorder) {
      try {
        const carrierOrder = extractCarrierOrderFromEvent(event);
        const movedCarrierId = extractMovedCarrierIdFromEvent(event);

        if (carrierOrder && carrierOrder.length > 0) {
          logger.info("[PlanEditor] Column moved - new carrier order:", {
            carrierOrder,
            movedCarrierId,
          });
          onCarrierReorder(carrierOrder, movedCarrierId);
        }
      } catch (error) {
        logger.error(
          "[PlanEditor] Error extracting carrier order from column move:",
          error as Error,
        );
      }
    }

    // Force row redraw after column reorder so that colSpan functions
    // re-evaluate against the new column positions. Without this, cells
    // that span multiple network sub-columns render stale/overlapping values.
    if (event.finished) {
      event.api.redrawRows();
    }
  },
  onColumnResized: saveGridState,
  onColumnVisible: (event) => {
    saveGridState?.();
    scheduleVisibilityReflow(event.api);
  },
  onColumnPinned: saveGridState,
  onSortChanged: saveGridState,
  onFilterChanged: saveGridState,
  onColumnGroupOpened: saveGridState,
  onCellValueChanged: registerOnCellValueChanged({
    onCoverageChange,
    onCommissionChange,
    coverage,
    getCurrentCoverage,
    isInternalUpdateRef,
    validateFieldFn,
    onManuallyEdited,
    onAiFieldEdited,
  }),
  onGridReady,
  getMainMenuItems,
  sideBar: sideBar ?? false,
});
